/**
 * Current design-token values for the mail viewer iframe (DESIGN.md §3, T10).
 *
 * The viewer is a sandboxed, opaque-origin iframe, so it cannot use the app's CSS variables. This
 * hook resolves the tokens on <html> and re-resolves them when the theme changes (`.dark` class,
 * density attribute, or a runtime brand theme <style> in <head>).
 */
import { useMemo, useSyncExternalStore } from 'react';
import type { MailTheme } from './sanitize';

function subscribe(onChange: () => void): () => void {
  if (typeof MutationObserver === 'undefined' || typeof document === 'undefined') return () => {};
  const observer = new MutationObserver(onChange);
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class', 'style', 'data-density'] });
  observer.observe(document.head, { childList: true });
  return () => observer.disconnect();
}

/** Serialized so React can compare snapshots by value; empty string = not resolvable (SSR, tests). */
function snapshot(): string {
  if (typeof document === 'undefined') return '';
  const root = getComputedStyle(document.documentElement);
  const token = (name: string) => root.getPropertyValue(name).trim();
  const background = token('--background');
  if (!background) return '';
  const theme: MailTheme = {
    colorScheme: document.documentElement.classList.contains('dark') ? 'dark' : 'light',
    background,
    foreground: token('--foreground'),
    mutedForeground: token('--muted-foreground'),
    link: token('--primary'),
    border: token('--border'),
    fontFamily: document.body ? getComputedStyle(document.body).fontFamily : '',
  };
  return JSON.stringify(theme);
}

export function useMailTheme(): MailTheme | undefined {
  const serialized = useSyncExternalStore(subscribe, snapshot, () => '');
  return useMemo(() => (serialized ? (JSON.parse(serialized) as MailTheme) : undefined), [serialized]);
}
